import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function ComprehensiveMigrationSolutions() {
  const features = [{
    id: 1,
    title: "Proven Migration Frameworks",
    description: "Repeatable, battle-tested processes tailored for mid-sized North American businesses migrating from Microsoft Dynamics GP to Dynamics 365 Business Central.",
    icon: CheckCircle
  }, {
    id: 2,
    title: "Audit-Ready Reporting",
    description: "Clean cheque remittance templates, comprehensive mapped data, and professionally branded outputs that meet stringent audit requirements.",
    icon: CheckCircle
  }, {
    id: 3,
    title: "Scalable Pricing Tiers",
    description: "Multiple packages based on migration complexity, starting at $7,500 CAD. Clear, transparent pricing in Canadian dollars for budget predictability.",
    icon: CheckCircle
  }, {
    id: 4,
    title: "Client-Ready Documentation",
    description: "Polished proposals, migration templates, and comprehensive onboarding materials that accelerate your transition and ensure stakeholder alignment.",
    icon: CheckCircle
  }];
  return <section className="relative overflow-hidden">
      {}
      <div className="bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-6 animate-fade-in-up">
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground mb-6 tracking-tight">
                Comprehensive Migration Solutions
              </h2>
              <p className="text-lg text-foreground/80 leading-relaxed mb-8">
                Fast, reliable ERP migration across North America. GP2BCExpress delivers precision-engineered solutions that transform your Microsoft Dynamics GP environment into a modern Dynamics 365 Business Central platform with minimal disruption.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/PricingPage">
                  <Button size="lg" className="shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
                    View Pricing Plans
                  </Button>
                </Link>
                <Link to="/ContactPage">
                  <Button variant="outline" size="lg" className="hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
                    Schedule Consultation
                  </Button>
                </Link>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-6 animate-fade-in-up-delay-100">
              <div className="relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1409748/img/migration_dashboard_overview.png" alt="Migration dashboard" className="rounded-lg shadow-strong w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="bg-muted-dark text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center mb-16 animate-fade-in-up">
            <h3 className="font-display text-3xl lg:text-4xl font-bold mb-4 tracking-tight">
              Built for Enterprise Migration Excellence
            </h3>
            <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed">
              Our comprehensive approach ensures your migration is smooth, audit-compliant, and delivers measurable business value from day one.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <div key={feature.id} data-index={i} className="col-span-12 md:col-span-6 animate-fade-in-up-delay-200">
                  <Card className="h-full bg-muted-dark/40 border-white/10 hover:border-white/20 hover:shadow-strong hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-xl text-white mb-2">
                            {feature.title}
                          </CardTitle>
                          <CardDescription className="text-white/70">
                            {feature.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </div>;
          })}
          </div>

          <div className="mt-16 text-center">
            <Link to="/OurServicesPage">
              <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
                Explore All Services
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {}
      <div className="bg-primary-light-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-5 animate-fade-in-up">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1409748/img/audit_ready_report_sample.png" alt="Audit-ready report sample" className="rounded-lg shadow-strong w-full" />
            </div>
            <div className="col-span-12 lg:col-span-7 animate-fade-in-up-delay-100">
              <h3 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-6 tracking-tight">
                Audit-Ready from Day One
              </h3>
              <p className="text-lg text-foreground/80 leading-relaxed mb-6">
                Every migration includes comprehensive documentation, data mapping reports, and branded templates that satisfy the most stringent audit requirements. Our frameworks ensure complete traceability and compliance.
              </p>
              <ul className="space-y-4">
                {["Complete data lineage tracking", "Automated reconciliation reports", "Custom branded output templates", "Detailed migration audit logs"].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground/90">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8">
                <Link to="/ContactPage">
                  <Button size="lg" className="shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
                    Request a Demo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}